import { useRouter } from 'next/router';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'next-i18next';
import { forwardRef, useContext, useMemo, useState } from 'react';
import i18nStringsContext from '@contexts/i18nStringsContext';
import {
  useValidateEmailIsUniqueMutation,
  useValidatePhoneNumberMutation,
} from '@shared/services/users';
import styles from './DefaultLandingPage.module.css';
import { phonePrefix } from '@utilities/helpers/common';
import { LicensePlateErrors, PaymentTypes } from '@shared/types/common';
import { useValidateLicensePlateMutation } from '@shared/services/common';
import { MembershipType, ValidateEmailRequest } from '@shared/types';
import {
  useCreateNewUserMutation,
  useRegisterCarsMutation,
} from '@features/Campaign/services/campaign';
import useAuth from '@features/MyAccount/hooks/use-auth';
import { useCompleteOrderUsingExistingCardMutation } from '@features/Checkout/services/checkout';
import { Snackbar, Alert as MuiAlert, Slide } from '@mui/material';
import Button from '@components/Button';
import {
  CampaignSteps,
  CreateNewUserRequest,
  ValidationSchemaProps,
} from '@shared/types/campaigns';
import CarRegistrationAccordion from '../CarRegistrationAccordion/CarRegistrationAccordion';
import usePayment from '@hooks/use-payment';
import { captureException } from '@sentry/nextjs';

const {
  DefaultCampaign: DefaultCampaignClass,
  Container,
  CampaignFormContainer,
} = styles;

export interface LandingPageFormValues {
  email: string;
  password: string;
  phone: string;
  licensePlate: string[];
}

interface DefaultLandingPageProps {
  submitButtonText?: string;
  additionalTerms?: string;
  agreementId?: string;
  allowMultipleCars?: boolean;
}

const DefaultLandingPage: React.FC<DefaultLandingPageProps> = ({
  submitButtonText,
  additionalTerms,
  agreementId,
  allowMultipleCars = false,
}) => {
  /*
   *   HOOKS
   */

  const { t } = useTranslation(['common', 'account', 'campaign']);
  const router = useRouter();

  const { fieldIsRequired } = useContext(i18nStringsContext);
  const phoneNumberPrefix = phonePrefix(router.locale);

  const {
    login,
    isAuthenticated,
    user,
    logOut,
    error: authError,
    reset,
  } = useAuth(false);

  const {
    email: emailQuery,
    lpn: licensePlateQuery,
    phone: phoneNumberQuery,
  } = router.query;

  /*
   *    HOOKS & VARIABLES
   */

  const phoneNumberQueryTyped = phoneNumberQuery as string | undefined;

  // + (Plus) is changed to space in the phone number query
  const phoneQueryHasPrefix = phoneNumberQueryTyped?.startsWith(' ');

  // To do: Add auto fill formik value if phone query exists (Also for email (email) and license plate (lpn))
  const sanitizedPhoneNumberQuery = phoneNumberQueryTyped
    ? phoneQueryHasPrefix
      ? `+${phoneNumberQueryTyped?.trim()}`
      : `${phoneNumberPrefix}${phoneNumberQueryTyped?.trim()}`
    : undefined;

  const [isSignUp, setIsSignUp] = useState<boolean>(true);
  const [membershipIds, setMembershipIds] = useState<string[]>([]);
  const [isSnackbarOpen, setisSnackbarOpen] = useState(true);
  const [currentStep, setCurrentStep] = useState<CampaignSteps>(
    CampaignSteps.USER_INFORMATION
  );

  const userCardCheck = Boolean(user?.has_card) && isAuthenticated;

  // If logged in, set isSignUp to false to hide sign-up form
  useMemo(() => {
    if (isAuthenticated) {
      setIsSignUp(false);
    } else {
      setIsSignUp(true);
    }
  }, [isAuthenticated]);

  /*
   *   API CALLS
   */

  const { sessionId } = usePayment({
    walletId: user?.wallet,
    paymentType: PaymentTypes.CHECKOUTSINGLEWASH,
    membershipsId: membershipIds,
    skipMembershipValidation: true,
    skip: !user || userCardCheck,
  });

  const [createNewUser, { data: createUserData, isLoading: isCreatingUser }] =
    useCreateNewUserMutation();

  const [validateEmailIsUnique] = useValidateEmailIsUniqueMutation();
  const [validatePhoneNumber] = useValidatePhoneNumberMutation();
  const [validateLicensePlate] = useValidateLicensePlateMutation();
  const [registerCar] = useRegisterCarsMutation();

  const [
    completeOrderUsingExistingCard,
    {
      isLoading: isCompletingOrderUsingExistingCard,
      isSuccess: finishedCompletingOrderUsingExistingCard,
    },
  ] = useCompleteOrderUsingExistingCardMutation();

  /*
   *   TRANSLATIONS FOR ERROR MESSAGES (FORM VALIDATION SCHEMA)
   */

  const errorMessages = {
    email: {
      required: t('invalidEmail', { ns: 'common' }),
      invalid: t('invalidEmail', { ns: 'common' }),
      alreadyExists: (
        <span>
          {t('youAlreadyHaveAProfile', { ns: 'campaign' })}
          <Button
            appearance="text"
            size="extra-small"
            type="button"
            onClick={() => setIsSignUp(!isSignUp)}
          >
            {t('login', { ns: 'common' })}
          </Button>
        </span>
      ),
    },
    password: {
      required: fieldIsRequired?.replace(
        '{fieldName}',
        t('password', { ns: 'common' })
      ),
      minChars: t('minPasswordLength', { ns: 'account' }),
      maxChars: t('maxPasswordLength', { ns: 'account' }),
    },
    phone: {
      required: fieldIsRequired?.replace(
        '{fieldName}',
        t('yourPhoneNumber', { ns: 'account' })
      ),
      invalid: t('invalidPhoneNumber', { ns: 'common' }),
    },
    licensePlate: {
      minLength: t('minPlateLength', { ns: 'common' }),
      maxLength: t('maxPlateLength', { ns: 'common' }),
      required: t('plateRequired', { ns: 'common' }),
      alreadyExists: t('licensePlateAlreadyExists', { ns: 'common' }),
    },
  };

  /*
   *   LOCAL COMPONENTS
   */

  const Alert = forwardRef((props, ref) => (
    <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
  ));

  /*
   *   HANDLERS
   */

  const handleClose = (event: any, reason: string) => {
    if (reason === 'clickaway') return;
    setisSnackbarOpen(false);
  };

  const handleLogin = (email: string, password: string) => {
    login({ email, password }, null, true);
  };

  // Function to get the validation schema for the landing page form
  const getLandingPageValidationSchema = ({
    isAuthenticated,
    isSignUp = true,
    phoneNumberPrefix,
    errorMessages,
  }: ValidationSchemaProps) => {
    const licensePlateValidation = Yup.string()
      .trim()
      .required(errorMessages.licensePlate.required)
      .test(
        'isUnique',
        errorMessages.licensePlate.alreadyExists,
        async function (value) {
          try {
            await validateLicensePlate({
              license_plate: value!,
              membershipType: MembershipType.UNLIMITED,
            }).unwrap();
            return true;
          } catch (error: any) {
            if (error.data?.response === LicensePlateErrors.ALREADY_EXISTS) {
              return this.createError({
                message: errorMessages.licensePlate.alreadyExists,
              });
            }
            if (error.data?.response === LicensePlateErrors.MINUMUM_LENGTH) {
              return this.createError({
                message: errorMessages.licensePlate.minLength,
              });
            }
            if (error.data?.response === LicensePlateErrors.MAXIMUM_LENGTH) {
              return this.createError({
                message: errorMessages.licensePlate.maxLength,
              });
            }
            console.error('Unhandled license plate validation error:', error);
            return this.createError({
              message: 'Something went wrong, please try again',
            });
          }
        }
      );

    return Yup.object({
      email: !isAuthenticated
        ? Yup.string()
            .required(errorMessages.email.required)
            .email(errorMessages.email.invalid)
            .test(
              'isUnique',
              errorMessages.email.alreadyExists,
              async function (value) {
                if (!value || !isSignUp) return true;
                try {
                  await validateEmailIsUnique(
                    value as unknown as ValidateEmailRequest
                  ).unwrap();
                  return true;
                } catch (error: any) {
                  if (error.data.message === 'Invalid e-mail') {
                    return this.createError({
                      message: errorMessages.email.invalid,
                    });
                  }
                }
              }
            )
        : Yup.string(),
      password:
        isSignUp && !isAuthenticated
          ? Yup.string()
              .required(errorMessages.password.required)
              .min(8, errorMessages.password.minChars)
              .max(72, errorMessages.password.maxChars)
          : Yup.string(),
      phone:
        isSignUp && !isAuthenticated
          ? Yup.string()
              .test(
                'isRequiredPhoneNumber',
                errorMessages.phone.required,
                function (value) {
                  if (!value || value === phoneNumberPrefix) return false;
                  return true;
                }
              )
              .test(
                'isValidPhoneNumber',
                errorMessages.phone.invalid,
                async function (value) {
                  if (!value) return true;
                  try {
                    await validatePhoneNumber(
                      phoneNumberPrefix + value.substring(3)
                    ).unwrap();
                    return true;
                  } catch (error) {
                    console.error(error);
                    return false;
                  }
                }
              )
          : Yup.string(),
      licensePlate:
        isSignUp || isAuthenticated
          ? Yup.array()
              .of(licensePlateValidation)
              .required(errorMessages.licensePlate.required)
              .test(
                'has-valid-plates',
                errorMessages.licensePlate.required,
                (plates) => {
                  return (
                    plates?.length > 0 &&
                    plates.some((plate) => plate && plate.trim() !== '')
                  );
                }
              )
          : Yup.array(),
    });
  };

  // Function to handle order completion using existing card
  const handleExistingCarOrder = async (membershipIds: string[]) => {
    try {
      const payload = await completeOrderUsingExistingCard({
        membershipIds: membershipIds,
        skip_mebership_validation: true,
      }).unwrap();

      router.push({
        pathname: '/checkout/confirmation/',
        query: {
          orderId: payload.orderId,
          newOrder: true,
        },
      });
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        captureException(
          new Error(`Failed to complete order: ${JSON.stringify(error)}`)
        );
      }
    }
  };

  // Function to handle registration of cars/license plates (Multiple plates are allowed)
  const handleRegisterCars = async (values: LandingPageFormValues) => {
    const registerCarPayload = {
      license_plate: values.licensePlate,
      agreements: agreementId || null,
    };

    await registerCar(registerCarPayload).then((response) => {
      if ('data' in response && 'cars_registered' in response.data) {
        const allMembershipIds = response.data.cars_registered;
        setMembershipIds(allMembershipIds);

        if (!userCardCheck) {
          setCurrentStep(CampaignSteps.PAYMENT_INFORMATION);
        } else {
          handleExistingCarOrder(allMembershipIds);
        }
      }
    });
  };

  /*
   *   FORM: FORMIK
   */

  const formik = useFormik<LandingPageFormValues>({
    initialValues: {
      email: (emailQuery as string) || '',
      password: '',
      phone: sanitizedPhoneNumberQuery || phoneNumberPrefix,
      licensePlate: licensePlateQuery ? [licensePlateQuery as string] : [''],
    },
    validateOnChange: allowMultipleCars ? false : true,
    validateOnBlur: allowMultipleCars ? false : true,
    enableReinitialize: true,
    validationSchema: getLandingPageValidationSchema({
      isAuthenticated,
      isSignUp,
      phoneNumberPrefix,
      errorMessages,
    }),
    onSubmit: async (values: LandingPageFormValues) => {
      const phoneNumberWithoutPrefix = values.phone.startsWith(
        phoneNumberPrefix
      )
        ? values.phone.slice(3)
        : values.phone;

      const isNewUser = isSignUp && !isAuthenticated;
      const isExistingUser = isAuthenticated;

      if (isNewUser) {
        const newUserPayload: CreateNewUserRequest = {
          email: values.email,
          consent: 1,
          identityProvider: 'Car Registration',
          phone: Number(phoneNumberWithoutPrefix),
          phone_prefix: phoneNumberPrefix.substring(1, 3),
          password: values.password,
        };

        await createNewUser(newUserPayload).then(async () => {
          handleRegisterCars(values);
        });
      } else if (isExistingUser) {
        handleRegisterCars(values);
      } else {
        handleLogin(values.email, values.password);
      }
    },
  });

  // Reset form when isSignUp changes
  useMemo(() => {
    formik.resetForm();
  }, [isSignUp]);

  return (
    <div className={DefaultCampaignClass}>
      {router.query?.error && (
        <Snackbar
          open={isSnackbarOpen}
          onClose={() => {
            setisSnackbarOpen(false);
          }}
          autoHideDuration={6000}
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          TransitionComponent={Slide}
        >
          <Alert
            onClose={handleClose}
            severity="error"
            sx={{ width: '100%', borderRadius: 0 }}
          >
            {router.query?.error}
          </Alert>
        </Snackbar>
      )}

      <div className={Container}>
        <div className={CampaignFormContainer}>
          <CarRegistrationAccordion
            formik={formik}
            isSignUp={isSignUp}
            submitButtonText={submitButtonText}
            additionalTerms={additionalTerms}
            isLoading={isCreatingUser}
            isLoadingExistingCard={
              isCompletingOrderUsingExistingCard ||
              finishedCompletingOrderUsingExistingCard
            }
            toggleForm={() => setIsSignUp(!isSignUp)}
            allowMultipleCars={allowMultipleCars}
            paymentSessionId={sessionId}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            userName={user?.name}
            userEmail={user?.email}
            logOut={logOut}
            phoneNumberPrefix={phoneNumberPrefix}
            resetAuth={reset}
            authError={authError}
            isLoggedIn={isAuthenticated && user}
            hasCard={Boolean(user?.has_card) && isAuthenticated}
            membershipIds={membershipIds}
          />
        </div>
      </div>
    </div>
  );
};

export default DefaultLandingPage;
